import {
  Component,
  OnDestroy,
  ViewChild,
  ViewEncapsulation,
} from "@angular/core";
import { MenuItem } from "primeng/api";
import { AssetContent, AssetViewModel } from "@helix/client";
import { BehaviorSubject, firstValueFrom, Subject } from "rxjs";
import { ThreeDModelPopupComponent } from "../../three-d-model-popup/three-d-model-popup.component";
import { MatterportViewerPopupComponent } from "../../matterport-viewer-popup/matterport-viewer-popup.component";
import { AssetDocumentPopupComponent } from "../../asset-document-popup/asset-document-popup.component";
import { ProcessModelPopupComponent } from "../../process-model-popup/process-model-popup.component";
import { AssetContentType } from "../../../../enums/asset-content.type";
import { AssetInfoPopupComponent } from "../../asset-info-popup/asset-info-popup.component";
import { AssetService } from "../../../../services/asset.service";
import { NbDialogService, NbToastrService } from "@nebular/theme";
import { ContextMenu } from "primeng/contextmenu";
import { PermissionTypes } from "../../../../enums/permissions.enum";
import { DocumentService } from "../../../../services/document.service";
import { AsyncPipe } from "@angular/common";

@Component({
  selector: "ngx-asset-tree-context-menu",
  templateUrl: "./asset-tree-context-menu.component.html",
  styleUrls: ["./asset-tree-context-menu.component.scss"],
  encapsulation: ViewEncapsulation.None,
  imports: [ContextMenu, AsyncPipe],
})
export class AssetTreeContextMenuComponent implements OnDestroy {
  @ViewChild("contextMenu", { static: true }) contextMenu: ContextMenu;

  public readonly items$: Subject<MenuItem[]> = new BehaviorSubject([]);
  private hasDocumentReadAccess$: BehaviorSubject<boolean>;
  private destroyed$ = new Subject<void>();
  constructor(
    private readonly assetService: AssetService,
    private readonly dialogService: NbDialogService,
    private readonly toastr: NbToastrService,
    private readonly documentService: DocumentService,
  ) {}

  filterContentType(
    content: AssetContent[],
    contentTypeId: AssetContentType,
  ): AssetContent[] {
    return content != null
      ? content.filter((item) => item.assetContentTypeId === contentTypeId)
      : null;
  }

  showAssetInformation(assetContent: AssetContent[], assetName: string): void {
    this.dialogService.open(AssetInfoPopupComponent, {
      context: { assetContent, assetName },
    });
  }

  show3dModels(assetContent: AssetContent[]): void {
    const threeDModelContent: AssetContent[] = this.filterContentType(
      assetContent,
      AssetContentType.ThreeDModel,
    );
    if (threeDModelContent == null || threeDModelContent.length === 0) {
      this.toastr.warning(
        "The asset does not contain 3D models",
        "No 3D models",
        { duration: 3000 },
      );
    } else {
      this.dialogService.open(ThreeDModelPopupComponent, {
        context: {
          threeDModelUrl: threeDModelContent[0].content,
          threeDAssetName: threeDModelContent[0].caption,
        },
      });
    }
  }

  showMatterport(assetContent: AssetContent[]): void {
    const matterportModelContent = this.filterContentType(
      assetContent,
      AssetContentType.MatterPortModel,
    );
    if (matterportModelContent == null || matterportModelContent.length === 0) {
      this.toastr.warning(
        "The asset does not contain Matterport models",
        "No Matterport models",
        { duration: 3000 },
      );
      return;
    }

    this.dialogService.open(MatterportViewerPopupComponent, {
      context: {
        matterportModelSid: matterportModelContent[0].content,
        matterportAssetName: matterportModelContent[0].caption,
      },
    });
  }

  showAssetDocuments(assetId: number, assetName: string): void {
    this.dialogService.open(AssetDocumentPopupComponent, {
      context: { assetId, assetName },
    });
  }

  showNetworkMap(assetContent: AssetContent[], assetName: string): void {
    const documentContent = this.filterContentType(
      assetContent,
      AssetContentType.NetworkMap,
    );
    if (documentContent == null || documentContent.length === 0) {
      this.toastr.warning(
        "The asset does not have a network map",
        "No network map",
      );
    } else {
      this.dialogService.open(ProcessModelPopupComponent, {
        context: {
          title: `${assetName} Network Map`,
          imageUrl: documentContent[0].content,
        },
      });
    }
  }

  private fetchAssetContent(item: AssetViewModel): Promise<AssetContent[]> {
    return firstValueFrom(this.assetService.getAssetContentById(item.assetId));
  }

  setMenuItems(asset: AssetViewModel) {
    this.items$.next([
      {
        label: "Loading...",
        disabled: true,
      },
    ]);

    Promise.all([this.fetchAssetContent(asset)]).then(([assetContent]) => {
      const items: MenuItem[] = [];

      const has = (type: AssetContentType) =>
        assetContent.some((x) => x.assetContentTypeId === type);

      if (has(AssetContentType.AssetInformation)) {
        items.push({
          label: "Asset Information",
          icon: "fa-solid fa-circle-info",
          command: () => this.showAssetInformation(assetContent, asset.name),
        });
      }

      if (has(AssetContentType.ThreeDModel)) {
        items.push({
          label: "3D Model",
          icon: "fa-solid fa-cube",
          command: () => this.show3dModels(assetContent),
        });
      }

      if (has(AssetContentType.NetworkMap)) {
        items.push({
          label: "Network Map",
          icon: "fa-solid fa-diagram-project",
          command: () => this.showNetworkMap(assetContent, asset.name),
        });
      }

      if (has(AssetContentType.MatterPortModel)) {
        items.push({
          label: "Matterport Model",
          icon: "matterport-icon",
          command: () => this.showMatterport(assetContent),
        });
      }

      if (
        this.documentService
          .getDocumentPermissions(PermissionTypes.Read)
          .getValue()
      ) {
        items.push({
          label: "Asset Documents",
          icon: "fa-solid fa-file-invoice",
          command: () => this.showAssetDocuments(asset.assetId, asset.name),
        });
      }

      if (items.length > 0) {
        items.push({ separator: true });
      }

      if (items.length === 0) {
        items.push({
          label: "This asset has no interactions",
          disabled: true,
        });
      }

      items.push({ separator: true });
      items.push({ label: "Cancel" });

      this.items$.next(items);
    });
  }
  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
